/**
 * Field Guide content registry — auto-discovers article modules via Vite's
 * `import.meta.glob` and serves loaders on demand.
 *
 * Convention:
 *   web/content/guide/<lang>/<section>/<slug>.ts
 *
 * Each module's default export must satisfy `GuideArticle`.  Returns
 * `null` when no module exists; the renderer falls back to skeleton state.
 */

import type { ArticleSlug, LangCode, SectionSlug } from './architecture';
import type { GuideArticle } from './content-types';

type ArticleModule = { default: GuideArticle };

const MODULES = import.meta.glob<ArticleModule>(
  '../../../content/guide/*/*/*.ts',
);

function pathFor(lang: LangCode, section: SectionSlug, slug: ArticleSlug): string {
  return `../../../content/guide/${lang}/${section}/${slug}.ts`;
}

export function findArticleLoader(
  lang: LangCode,
  section: SectionSlug,
  slug: ArticleSlug,
): (() => Promise<ArticleModule>) | null {
  const key = pathFor(lang, section, slug);
  return MODULES[key] ?? null;
}

/**
 * List every published (lang, section, slug) triple.  Drives the sitemap
 * generator + prerender list without manual edits.
 */
export function listPublishedArticles(): Array<{
  lang: string; section: string; slug: string;
}> {
  const out: Array<{ lang: string; section: string; slug: string }> = [];
  const re = /\/content\/guide\/([^/]+)\/([^/]+)\/([^/]+)\.ts$/;
  for (const key of Object.keys(MODULES)) {
    const m = key.match(re);
    if (!m) continue;
    const [, lang, section, slug] = m;
    out.push({ lang, section, slug });
  }
  return out;
}
